import { useState, useCallback, useEffect } from 'react';
import { useDevice } from '../../context/DeviceContext';
import styles from './ScenarioEditableField.module.css';
import { ScenarioEditFieldButton } from './ScenarioCommonButtons';
import ScenarioInPlaceEdit from './ScenarioInPlaceEdit';
import ReactDOM from 'react-dom';

interface ScenarioEditableFieldProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  label?: string;
  className?: string;
  maxLength?: number;
  renderDisplay?: (value: string) => React.ReactNode;
}

const ScenarioEditableField = ({
  value,
  onSave,
  label,
  className = '',
  maxLength,
  renderDisplay = (val) => <span>{val}</span>
}: ScenarioEditableFieldProps): JSX.Element => {
  const { isMobile } = useDevice();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async (newValue: string) => {
    if (newValue === value) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      await onSave(newValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      // You might want to show an error toast/notification here
    } finally {
      setIsLoading(false);
    }
  }, [value, onSave]);

  // Render mobile overlay using portal to ensure it's at the root level
  const renderMobileOverlay = () => {
    if (isMobile && isEditing) {
      return ReactDOM.createPortal(
        <div className={`${styles.mobileEditOverlay}`}>
          <ScenarioInPlaceEdit
            content={value}
            onCancel={handleCancel}
            onSave={handleSave}
            isUserMessage={false}
            maxLength={maxLength}
          />
        </div>,
        document.body
      );
    }
    return null;
  };

  // Prevent body scrolling when mobile overlay is active
  useEffect(() => {
    if (isMobile && isEditing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isEditing]);

  return (
    <>
      <div className={`${styles.container} ${className}`}>
        <div className={styles.content}>
          {isEditing && !isMobile ? (
            <ScenarioInPlaceEdit
              content={value}
              onCancel={handleCancel}
              onSave={handleSave}
              isUserMessage={false}
              maxLength={maxLength}
            />
          ) : (
            <>
              {renderDisplay(value)}
              <ScenarioEditFieldButton
                onClick={handleEditClick}
                showPill={false}
                label={label}
              />
            </>
          )}
        </div>
      </div>
      {renderMobileOverlay()}
    </>
  );
};

export default ScenarioEditableField; 